// ./src/utils/api/EmailLayoutService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { SYSTEM_ACCOUNT_ID } from "@/config";

// GraphQL queries
const EMAIL_LAYOUT_LIST_QUERY = gql`
  query EmailLayoutList(
    $filter: EmailLayoutFilter
    $params: PaginationAndSorting
  ) {
    emailLayoutList(filter: $filter, params: $params) {
      code
      data {
        id
        accountId
        name
        description
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_LAYOUT_GET_QUERY = gql`
  query EmailLayoutGet($id: ID) {
    emailLayoutGet(id: $id) {
      code
      data {
        id
        accountId
        name
        description
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        status
        version
        rights
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_LAYOUT_GET_MANY_QUERY = gql`
  query EmailLayoutGetMany($ids: [ID]) {
    emailLayoutGetMany(ids: $ids) {
      code
      data {
        id
        accountId
        name
        description
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        status
        version
        rights
        createdBy
        updatedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_LAYOUT_PREVIEW_QUERY = gql`
  query EmailLayoutPreview($id: ID, $sampleData: String) {
    emailLayoutPreview(id: $id, sampleData: $sampleData) {
      code
      data {
        id
        accountId
        name
        description
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        status
        rights
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const EMAIL_LAYOUT_CREATE_MUTATION = gql`
  mutation EmailLayoutCreate($params: EmailLayoutInput) {
    emailLayoutCreate(params: $params) {
      code
      data {
        id
        accountId
        name
        description
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        status
        version
        rights
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_LAYOUT_UPDATE_MUTATION = gql`
  mutation EmailLayoutUpdate(
    $where: EmailLayoutWhereInput
    $params: EmailLayoutInput
  ) {
    emailLayoutUpdate(where: $where, params: $params) {
      code
      data {
        id
        accountId
        name
        description
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        status
        version
        rights
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_LAYOUT_REMOVE_MUTATION = gql`
  mutation EmailLayoutRemove($where: EmailLayoutWhereInput) {
    emailLayoutRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_LAYOUT_REMOVE_MANY_MUTATION = gql`
  mutation EmailLayoutRemoveMany($where: [EmailLayoutWhereInput]) {
    emailLayoutRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_LAYOUT_PUBLISH_MUTATION = gql`
  mutation EmailLayoutPublish($where: EmailLayoutWhereInput) {
    emailLayoutPublish(where: $where) {
      code
      data {
        id
        accountId
        name
        description
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        status
        version
        rights
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface EmailLayout {
  id?: string | undefined;
  accountId?: string | undefined;
  name?: string | undefined;
  description?: string | undefined;
  htmlContent?: string | undefined;
  textContent?: string | undefined;
  fromName?: string | undefined;
  fromEmail?: string | undefined;
  replyToEmail?: string | undefined;
  status?: number | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  removedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  userCreated?: User;
  userUpdated?: User;
  userRemoved?: User;
}

export interface EmailLayoutInput {
  id?: string | null | undefined;
  accountId?: string | null | undefined;
  name?: string | null | undefined;
  description?: string | null | undefined;
  htmlContent?: string | null | undefined;
  textContent?: string | null | undefined;
  fromName?: string | null | undefined;
  fromEmail?: string | null | undefined;
  replyToEmail?: string | null | undefined;
  status?: number | null | undefined;
}

export interface EmailLayoutFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  name?: string[] | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface EmailLayoutWhereInput {
  id?: string | undefined;
}

/**
 * Check if an email layout is a system layout (read-only)
 */
export const isSystemLayout = (layout: EmailLayout): boolean => {
  return layout.accountId === SYSTEM_ACCOUNT_ID;
};

/**
 * Email Layout service that handles all email layout-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 * Also supports preview functionality
 */
export class EmailLayoutService {
  /**
   * Get a list of email layouts with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: EmailLayoutFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(EMAIL_LAYOUT_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single email layout by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(EMAIL_LAYOUT_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple email layouts by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(EMAIL_LAYOUT_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Preview an email layout with sample data
   * @param id - Layout ID to preview
   * @param sampleData - JSON string with sample variables (e.g., { "messageBody": "<p>Test</p>" })
   */
  static async preview(id: string, sampleData?: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(EMAIL_LAYOUT_PREVIEW_QUERY, {
      id: id,
      sampleData: sampleData || null,
    });
    return result;
  }

  /**
   * Create a new email layout
   */
  static async create(params: EmailLayoutInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EMAIL_LAYOUT_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing email layout
   */
  static async update(
    where: EmailLayoutWhereInput,
    params: EmailLayoutInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EMAIL_LAYOUT_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing email layout
   */
  static async publish(where: EmailLayoutWhereInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EMAIL_LAYOUT_PUBLISH_MUTATION,
      {
        where,
      }
    );
    return result;
  }

  /**
   * Remove/delete an email layout
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EMAIL_LAYOUT_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple email layouts
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      EMAIL_LAYOUT_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
